import axios from 'axios';
import { notifyForbidden } from './forbiddenNotifier';

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  validateStatus: function (status) {
    return status >= 200 && status < 600; // Aceita status codes de 200-599
  }
});

// Variável para controlar refresh em andamento e evitar múltiplas chamadas simultâneas
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Função centralizada para fazer refresh do token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  
  if (!refreshToken) {
    throw new Error('Refresh token não encontrado');
  }

  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
      refresh_token: refreshToken
    });

    if (response.status === 200 && response.data?.access_token) {
      const { access_token, refresh_token } = response.data;
      
      // Atualizar access_token
      localStorage.setItem('access_token', access_token);
      
      // Atualizar refresh_token se a API retornar um novo
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      return access_token;
    } else {
      throw new Error('Resposta inválida do servidor');
    }
  } catch (error) {
    // Limpar tokens em caso de erro
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    throw error;
  }
};

// Verifica se o token está expirado
const isTokenExpired = (token) => {
  try {
    const tokenData = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = tokenData.exp;
    const now = Math.floor(Date.now() / 1000);
    // Considera expirado se faltar menos de 60 segundos (margem de segurança)
    return expirationTime <= (now + 60);
  } catch (error) {
    return true; // Se não conseguir decodificar, considera expirado
  }
};

// Add token to requests
client.interceptors.request.use(async config => {
  const token = localStorage.getItem('access_token');
 
  if (token) {
    // Verifica se o token está expirado ou próximo de expirar
    if (isTokenExpired(token)) {
      // Se já está fazendo refresh, aguarda
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      // Inicia o processo de refresh
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (error) {
        isRefreshing = false;
        processQueue(error, null);
        // Redireciona para login em caso de erro
        window.location.href = '/';
        return Promise.reject(error);
      }
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle token refresh and 403 redirect
client.interceptors.response.use(
  response => {
    // 403: avisa o usuário via modal global (validateStatus aceita 4xx/5xx, então chega aqui)
    if (response.status === 403) {
      notifyForbidden();
      return Promise.reject(Object.assign(new Error('Forbidden: sem permissão'), { isForbidden: true }));
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // 403 vindo como erro de resposta
    if (error.response?.status === 403) {
      notifyForbidden();
      error.isForbidden = true;
      return Promise.reject(error);
    }
    
    // Handle 401 - Token expirado ou inválido
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Se já está fazendo refresh, aguarda
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ 
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(client(originalRequest));
            },
            reject 
          });
        });
      }

      // Inicia o processo de refresh
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);
        
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        
        // Limpar tokens e redirecionar para login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Devolve um access_token válido para chamadas que não passam pelo axios
 * (ex.: fetch com streaming SSE do assistente de IA). Reaproveita o mesmo
 * refresh/fila do interceptor acima. Retorna null se não houver sessão.
 * `force` ignora a validade local e renova (usado após um 401).
 */
export const getFreshAccessToken = async ({ force = false } = {}) => {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  if (!force && !isTokenExpired(token)) return token;

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await refreshAccessToken();
    isRefreshing = false;
    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    isRefreshing = false;
    processQueue(error, null);
    return null;
  }
};

export const httpClient = {
  async get(url, config = {}) {
    const response = await client.get(url, config);
    return response;
  },

  async post(url, data, config = {}) {
    const response = await client.post(url, data, config);
    return response;
  },

  async put(url, data, config = {}) {
    const response = await client.put(url, data, config);
    return response;
  },

  async patch(url, data, config = {}) {
    const response = await client.patch(url, data, config);
    return response;
  },

  async delete(url, config = {}) {
    const response = await client.delete(url, config);
    return response;
  },

  async postFile(url, data, config = {}) {
    const response = await client.post(url, data, config);
    return response;
  },

  async downloadFile(url, config = {title: 'download', extension: 'csv'}) {
    // Separar opções do axios das opções de download
    const { title, extension, ...axiosConfig } = config;
    
    // Garantir que responseType seja 'blob' se não especificado
    const requestConfig = {
      responseType: 'blob',
      ...axiosConfig
    };
    
    const response = await client.get(url, requestConfig);
    
    // Se response.data já for um Blob, usar diretamente; caso contrário, criar um novo
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const urlBlob = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = urlBlob;
    link.download = `${title || 'download'}.${extension || 'csv'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlBlob);
    return response;
  },
  
};

